import React from 'react';
import { IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import CopyButton from '../CopyButton';

const toHref = (value) => (/^https?:\/\//i.test(value) ? value : `https://${value}`);

// TextField that, when `readOnly`, stays selectable (unlike `disabled`) and shows
// a copy button – plus an open-link button for URLs – so values can be reused.
const DetailField = ({ readOnly = false, link = false, InputProps, inputProps, SelectProps, sx, ...props }) => {
  if (!readOnly) {
    return <TextField InputProps={InputProps} inputProps={inputProps} SelectProps={SelectProps} sx={sx} {...props} />;
  }

  const value = props.value;
  const text = Array.isArray(value) ? value.join(', ') : value == null ? '' : String(value);
  const showActions = Boolean(text) && !props.select;

  return (
    <TextField
      {...props}
      sx={{
        '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
        '& .MuiSelect-icon': { display: 'none' },
        ...sx,
      }}
      InputProps={{
        ...InputProps,
        readOnly: true,
        endAdornment: showActions ? (
          <InputAdornment position="end" sx={props.multiline ? { alignSelf: 'flex-start', mt: 1.5 } : undefined}>
            <Stack direction="row" spacing={0.25}>
              {link && (
                <Tooltip title="Open link" placement="top">
                  <IconButton
                    size="small"
                    component="a"
                    href={toHref(text)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open link"
                    sx={{ p: 0.4, color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                  >
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <CopyButton value={text} label={`Copy ${props.label ? props.label.toLowerCase() : 'value'}`} />
            </Stack>
          </InputAdornment>
        ) : InputProps?.endAdornment,
      }}
      inputProps={{ ...inputProps, readOnly: true }}
      // Empty values read as "—" instead of showing example placeholders or a date mask
      type={props.type === 'date' && !text ? 'text' : props.type}
      placeholder="—"
      InputLabelProps={{ ...props.InputLabelProps, shrink: true }}
      SelectProps={{
        ...SelectProps,
        readOnly: true,
        displayEmpty: true,
        renderValue: (selected) => selected || <span style={{ color: '#94a3b8' }}>—</span>,
      }}
      helperText={props.error ? props.helperText : undefined}
      required={false}
    />
  );
};

export default DetailField;
